import md5 from 'md5'
import { TextContent, TextItem } from 'pdfjs-dist/types/src/display/api'

import { approxSearchBest, fuzzyFindPosition } from '@/lib/assessment/quote-matching'
import { escapeRegExp, normalizeText } from '@/lib/string/string-utils'

export type HighlightRange = { start: number; end: number }

export type HighlightRangesMap = Map<string, HighlightRange[]>

export const getItemKey = (item: TextItem): string => {
  const strPart = item.str.substring(0, 10)
  return `${item.transform.join('_')}|${strPart}|${item.width}x${item.height}`
}

type ItemMapEntry = {
  item: TextItem
  start: number
  end: number
  startNoSpaces: number
  endNoSpaces: number
}

type CalculateHighlightDataResult = {
  highlightRangesMap: HighlightRangesMap
  firstHighlightId: string | null
}

// A single matched range broken down into the PDF.js text items it spans.
type MatchItem = { itemKey: string; range: HighlightRange }

// One occurrence of a quote on the page: where it starts in the page's fullText
// and the per-item ranges that need highlighting for it.
type Occurrence = { start: number; items: MatchItem[] }

// Characters of the quote's context kept on each side of the quote to form the
// "anchored needle" used to locate the correct occurrence. Enough to capture the
// immediate neighbouring text (e.g. "…within the SLA." before "Exceptions noted")
// that distinguishes the cited cell from look-alike cells elsewhere on the page.
const ANCHOR_PADDING = 80

const normalizeContextNeedle = (context: string): string =>
  normalizeText(context).toLowerCase().replace(/\s+/g, ' ').trim()

// Identifies the text items (e.g., from PDF.js) that overlap a [matchStart, matchEnd)
// span in the page's fullText, and the character ranges to highlight within each.
// Pure: it computes ranges but does not mutate the shared highlight map.
const computeMatchItems = (
  matchStart: number,
  matchEnd: number,
  itemMap: ReadonlyArray<ItemMapEntry>,
): MatchItem[] => {
  const result: MatchItem[] = []

  for (const mapEntry of itemMap) {
    const overlapStart = Math.max(mapEntry.start, matchStart)
    const overlapEnd = Math.min(mapEntry.end, matchEnd)

    if (overlapStart < overlapEnd) {
      const itemText = mapEntry.item.str
      const itemHighlightStart = Math.max(0, overlapStart - mapEntry.start)
      const itemHighlightEnd = Math.min(itemText.length, overlapEnd - mapEntry.start)

      if (itemHighlightStart < itemHighlightEnd) {
        result.push({
          itemKey: getItemKey(mapEntry.item),
          range: { start: itemHighlightStart, end: itemHighlightEnd },
        })
      }
    }
  }

  return result
}

// Writes an occurrence's per-item ranges into the shared map. Returns the first
// item key (in reading order) so callers can track the scroll anchor.
const commitMatchItems = (items: MatchItem[], map: HighlightRangesMap): string | null => {
  for (const { itemKey, range } of items) {
    if (!map.has(itemKey)) {
      map.set(itemKey, [])
    }
    map.get(itemKey)!.push(range)
  }
  return items.length > 0 ? items[0].itemKey : null
}

const findExactOccurrences = (
  quoteLower: string,
  fullText: string,
  itemMap: ReadonlyArray<ItemMapEntry>,
): Occurrence[] => {
  const occurrences: Occurrence[] = []
  const exactRegex = new RegExp(escapeRegExp(quoteLower), 'gi')

  let match
  while ((match = exactRegex.exec(fullText)) !== null) {
    const items = computeMatchItems(match.index, match.index + quoteLower.length, itemMap)
    if (items.length > 0) {
      occurrences.push({ start: match.index, items })
    }
    if (match.index === exactRegex.lastIndex) {
      exactRegex.lastIndex++ // Prevent infinite loops on zero-length matches
    }
  }

  return occurrences
}

// Fallback matcher that ignores spacing differences between the quote and the
// PDF text layer, mapping no-space match positions back to fullText coordinates.
const findNoSpacesOccurrences = (
  quoteNoSpacesLower: string,
  fullTextNoSpacesLower: string,
  itemMap: ReadonlyArray<ItemMapEntry>,
): Occurrence[] => {
  const occurrences: Occurrence[] = []
  const noSpacesRegex = new RegExp(escapeRegExp(quoteNoSpacesLower), 'gi')

  let noSpacesMatch
  while ((noSpacesMatch = noSpacesRegex.exec(fullTextNoSpacesLower)) !== null) {
    const noSpacesMatchIndex = noSpacesMatch.index
    const noSpacesMatchEndIndex = noSpacesMatchIndex + quoteNoSpacesLower.length

    let originalTextStartIndex = -1
    let originalTextEndIndex = -1

    for (let i = 0; i < itemMap.length; i++) {
      const { startNoSpaces, endNoSpaces, item, start } = itemMap[i]
      const itemText = item.str

      if (
        originalTextStartIndex === -1 &&
        noSpacesMatchIndex >= startNoSpaces &&
        noSpacesMatchIndex < endNoSpaces
      ) {
        const relativeStartPos = noSpacesMatchIndex - startNoSpaces
        let noSpaceCount = 0
        for (let j = 0; j < itemText.length; j++) {
          if (itemText[j] !== ' ') {
            if (noSpaceCount === relativeStartPos) {
              originalTextStartIndex = start + j
              break
            }
            noSpaceCount++
          }
        }
      }

      if (
        originalTextEndIndex === -1 &&
        noSpacesMatchEndIndex > startNoSpaces &&
        noSpacesMatchEndIndex <= endNoSpaces
      ) {
        const relativeEndPos = noSpacesMatchEndIndex - startNoSpaces
        let noSpaceCount = 0
        for (let j = 0; j < itemText.length; j++) {
          if (itemText[j] !== ' ') {
            noSpaceCount++
          }
          if (noSpaceCount === relativeEndPos) {
            originalTextEndIndex = start + j + 1
            break
          }
        }
      }
    }

    if (originalTextStartIndex !== -1 && originalTextEndIndex !== -1) {
      const items = computeMatchItems(originalTextStartIndex, originalTextEndIndex, itemMap)
      if (items.length > 0) {
        occurrences.push({ start: originalTextStartIndex, items })
      }
    }
    if (noSpacesMatch.index === noSpacesRegex.lastIndex) {
      noSpacesRegex.lastIndex++ // Prevent infinite loops
    }
  }

  return occurrences
}

// When a quote occurs multiple times on a page (e.g. "Exceptions noted" also
// matches inside "No exceptions noted"), pick the occurrence the AI actually
// cited. We locate the quote within its context, build a tight needle of the
// quote plus its immediate neighbours, fuzzily find that needle on the page, and
// return the occurrence sitting inside the matched region. Returns null when the
// context doesn't locate a region, so the caller can fall back safely.
const pickOccurrenceByContext = (
  occurrences: Occurrence[],
  quote: string,
  context: string,
  fullText: string,
): Occurrence | null => {
  const quoteInContext = fuzzyFindPosition(quote, context)
  const needle = quoteInContext
    ? normalizeContextNeedle(
        context.slice(
          Math.max(0, quoteInContext.start - ANCHOR_PADDING),
          quoteInContext.end + ANCHOR_PADDING,
        ),
      )
    : normalizeContextNeedle(context)

  const region = approxSearchBest(fullText, needle)
  if (!region) return null

  let best: Occurrence | null = null
  let bestDistance = Infinity
  for (const occurrence of occurrences) {
    const distance =
      occurrence.start < region.start
        ? region.start - occurrence.start
        : occurrence.start > region.end
          ? occurrence.start - region.end
          : 0
    if (distance < bestDistance) {
      bestDistance = distance
      best = occurrence
    }
  }

  return best
}

export const calculateHighlightData = (
  textContent: TextContent,
  quotes: string[],
  link: string,
  page: number,
  // Optional, index-parallel to `quotes`: the surrounding context the backend
  // captured around each quote. When provided, it disambiguates which occurrence
  // of a quote to highlight on pages where the bare quote matches several places.
  contexts?: string[],
  instanceId?: string,
): CalculateHighlightDataResult => {
  const newHighlightRangesMap: HighlightRangesMap = new Map()
  let firstMatchedItemKey: string | null = null
  let overallFirstMatchFound = false

  const items = textContent.items.filter(
    (item): item is TextItem => 'str' in item && item.str.trim().length > 0,
  )

  let fullText = ''
  let fullTextNoSpaces = ''
  const itemMap: ItemMapEntry[] = []

  items.forEach((item) => {
    let currentItemStartInFullText = fullText.length
    const currentItemStartInNoSpacesText = fullTextNoSpaces.length

    const textToAdd = normalizeText(item.str)
    if (fullText.length > 0 && !/\s$/.test(fullText) && !/^\s/.test(textToAdd)) {
      fullText += ' '
      currentItemStartInFullText++
    }

    fullText += textToAdd
    fullTextNoSpaces += textToAdd.replace(/\s+/g, '')

    const currentItemEndInFullText = fullText.length
    const currentItemEndInNoSpacesText = fullTextNoSpaces.length

    itemMap.push({
      item,
      start: currentItemStartInFullText,
      end: currentItemEndInFullText,
      startNoSpaces: currentItemStartInNoSpacesText,
      endNoSpaces: currentItemEndInNoSpacesText,
    })
  })

  fullText = fullText.toLowerCase()
  const fullTextNoSpacesLower = fullTextNoSpaces.toLowerCase()

  for (let quoteIndex = 0; quoteIndex < quotes.length; quoteIndex++) {
    const quoteLower = normalizeText(quotes[quoteIndex]).toLowerCase()
    if (!quoteLower) continue

    const quoteNoSpacesLower = quoteLower.replace(/\s+/g, '')
    const context = contexts?.[quoteIndex]
    const hasContext = !!context && context.trim().length > 0

    let occurrences = findExactOccurrences(quoteLower, fullText, itemMap)
    if (occurrences.length === 0) {
      occurrences = findNoSpacesOccurrences(quoteNoSpacesLower, fullTextNoSpacesLower, itemMap)
    }

    let chosen: Occurrence[]
    if (occurrences.length === 0) {
      // The quote text isn't contiguous on the page (e.g. split across table
      // cells in the PDF text layer). If we have a context, anchor to the region
      // it matches so the viewer still scrolls to the right area instead of the
      // top of the page.
      chosen = []
      if (hasContext) {
        const region = approxSearchBest(fullText, normalizeContextNeedle(context))
        if (region && region.end > region.start) {
          const items = computeMatchItems(region.start, region.end, itemMap)
          if (items.length > 0) chosen = [{ start: region.start, items }]
        }
      }
    } else if (hasContext && occurrences.length > 1) {
      const picked = pickOccurrenceByContext(occurrences, quotes[quoteIndex], context, fullText)
      // Narrow to the cited occurrence when the context locates one. Otherwise
      // keep the legacy "highlight all" behavior so a stale or unhelpful context
      // never hides a valid match.
      chosen = picked ? [picked] : occurrences
    } else {
      // No context to disambiguate (or a single occurrence): preserve the
      // existing behavior of highlighting every match.
      chosen = occurrences
    }

    for (const occurrence of chosen) {
      const firstItemKey = commitMatchItems(occurrence.items, newHighlightRangesMap)
      if (!overallFirstMatchFound && firstItemKey) {
        firstMatchedItemKey = firstItemKey
        overallFirstMatchFound = true
      }
    }
  }

  let finalFirstHighlightId: string | null = null
  if (firstMatchedItemKey) {
    const firstItemEntry = itemMap.find((entry) => getItemKey(entry.item) === firstMatchedItemKey)
    if (firstItemEntry && firstItemEntry.item.width > 0 && firstItemEntry.item.height > 0) {
      finalFirstHighlightId = getHighlightId(link, page, instanceId)
    }
  }

  return {
    highlightRangesMap: newHighlightRangesMap,
    firstHighlightId: finalFirstHighlightId,
  }
}

export const getHighlightId = (link: string, page?: number, instanceId?: string): string => {
  return `highlight-${page}-${md5(link)}${instanceId ? `-${instanceId}` : ''}`
}
